import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Plus,
  X,
  Loader2,
  Camera,
  Wrench,
  DollarSign,
  FileText,
  BarChart3,
  Upload,
  Check,
  UserCircle,
  Package,
  Truck,
  FileCheck,
  Receipt,
  Building2,
  Calendar,
  Clock,
  Edit3,
  Trash2,
  AlertTriangle,
  Save,
  Users,
  Target,
  CheckCircle2,
  AlertCircle,
  Pause,
  CheckSquare,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Helper function for currency formatting
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// ================= UPDATE PROGRESS MODAL =================
// Matches the user's original design with simpler layout

export const UpdateProgressModal = ({ milestones, projectId, onClose, onSaved, onAddMilestone }) => {
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Load milestone data when selected
  useEffect(() => {
    if (selectedMilestone) {
      const milestone = milestones.find(m => m.milestone_id === selectedMilestone);
      if (milestone) {
        setProgressPercent(milestone.progress_percent || 0);
        setNotes(milestone.notes || '');
      }
    }
  }, [selectedMilestone, milestones]);
  
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    // Preview photos (in real implementation, would upload to server)
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, { name: file.name, preview: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };
  
  const handleSubmit = async () => {
    if (!selectedMilestone) {
      toast.error("Please select a milestone");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.patch(
        `${API_BASE}/api/milestones/${selectedMilestone}`,
        { 
          progress_percent: progressPercent,
          status: progressPercent >= 100 ? 'complete' : progressPercent > 0 ? 'in_progress' : 'not_started',
          notes 
        },
        { withCredentials: true }
      );
      toast.success("Progress updated!");
      onSaved();
    } catch (error) {
      toast.error("Failed to update progress");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Wrench className="h-5 w-5 text-emerald-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Update Progress</h3>
          </div>
          <button 
            onClick={onAddMilestone}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-all"
          >
            <Plus className="h-4 w-4" />
            Add Milestone
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Select Milestone */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Select Milestone</label>
            <select
              value={selectedMilestone}
              onChange={(e) => setSelectedMilestone(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
            >
              <option value="">Choose a milestone to update...</option>
              {milestones.map(m => (
                <option key={m.milestone_id} value={m.milestone_id}>
                  {m.title} {m.status === 'complete' ? '(✓ Complete)' : m.progress_percent ? `(${m.progress_percent}%)` : ''}
                </option>
              ))}
            </select>
          </div>
          
          {/* Progress Slider (only show when milestone selected) */}
          {selectedMilestone && (
            <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium text-slate-300">Progress</label>
                <span className="text-lg font-bold text-emerald-400">{progressPercent}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={progressPercent}
                onChange={(e) => setProgressPercent(parseInt(e.target.value))}
                className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-2">
                <span>Not Started</span>
                <span>In Progress</span>
                <span>Complete</span>
              </div>
            </div>
          )}
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any notes about this progress update..."
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
            />
          </div>
          
          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Add progress photos</label>
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center cursor-pointer hover:border-slate-600 transition-colors"
            >
              <Camera className="h-8 w-8 mx-auto mb-2 text-slate-500" />
              <p className="text-sm text-slate-400">Click to upload photos</p>
              <p className="text-xs text-slate-500 mt-1">Coming soon</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
            
            {/* Photo Previews */}
            {photos.length > 0 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative flex-shrink-0">
                    <img src={photo.preview} alt={photo.name} className="w-16 h-16 object-cover rounded-lg" />
                    <button 
                      onClick={() => setPhotos(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !selectedMilestone}
            className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? 'Updating...' : 'Update Progress'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= ADD MILESTONE MODAL =================

export const AddMilestoneModal = ({ projectId, milestones, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [estimatedCost, setEstimatedCost] = useState('');
  const [assignedSubName, setAssignedSubName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Please enter a milestone title");
      return;
    }
    if (!targetDate) {
      toast.error("Please set a target completion date");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/projects/${projectId}/milestones`,
        {
          title: title.trim(),
          description: description.trim(),
          start_date: startDate,
          target_date: targetDate,
          estimated_cost: estimatedCost ? parseFloat(estimatedCost) : 0,
          assigned_sub_name: assignedSubName || null,
          status: 'not_started',
          progress_percent: 0,
          order: milestones.length + 1
        },
        { withCredentials: true }
      );
      toast.success("Milestone created!");
      onSaved();
    } catch (error) {
      toast.error("Failed to create milestone");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Wrench className="h-5 w-5 text-emerald-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Add New Milestone</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Milestone Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Foundation Complete"
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What work is included?"
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Target Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Estimated Cost</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="number"
                  value={estimatedCost}
                  onChange={(e) => setEstimatedCost(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Assign To</label>
              <input
                type="text"
                value={assignedSubName}
                onChange={(e) => setAssignedSubName(e.target.value)}
                placeholder="Subcontractor name"
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
              />
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !targetDate}
            className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {submitting ? 'Creating...' : 'Create Milestone'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= EDIT MILESTONE MODAL (COMPREHENSIVE) =================

export const EditMilestoneModal = ({ milestone, projectId, subcontractors = [], onClose, onSaved, onDeleted }) => {
  // Tabs for different editing sections
  const [activeTab, setActiveTab] = useState('details');
  
  // Core milestone fields
  const [title, setTitle] = useState(milestone?.title || '');
  const [description, setDescription] = useState(milestone?.description || '');
  const [startDate, setStartDate] = useState(milestone?.start_date || '');
  const [targetDate, setTargetDate] = useState(milestone?.target_date || '');
  const [actualCompletionDate, setActualCompletionDate] = useState(milestone?.actual_completion_date || '');
  const [status, setStatus] = useState(milestone?.status || 'not_started');
  const [progressPercent, setProgressPercent] = useState(milestone?.progress_percent || 0);
  
  // Financial fields
  const [estimatedCost, setEstimatedCost] = useState(milestone?.estimated_cost || 0);
  const [actualCost, setActualCost] = useState(milestone?.actual_cost || 0);
  
  // Assignment fields
  const [assignedSubId, setAssignedSubId] = useState(milestone?.assigned_sub_id || '');
  const [assignedSubName, setAssignedSubName] = useState(milestone?.assigned_sub_name || '');
  const [assignedSubPhone, setAssignedSubPhone] = useState(milestone?.assigned_sub_phone || '');
  
  // Notes
  const [notes, setNotes] = useState(milestone?.notes || '');
  
  // Priority & Order
  const [order, setOrder] = useState(milestone?.order || 1);
  
  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Status options with icons and colors
  const statusOptions = [
    { value: 'not_started', label: 'Not Started', color: 'bg-slate-600', icon: Clock },
    { value: 'in_progress', label: 'In Progress', color: 'bg-blue-600', icon: Target },
    { value: 'complete', label: 'Complete', color: 'bg-emerald-600', icon: CheckCircle2 },
    { value: 'on_hold', label: 'On Hold', color: 'bg-amber-600', icon: Pause },
    { value: 'delayed', label: 'Delayed', color: 'bg-red-600', icon: AlertCircle },
  ];
  
  // Handle subcontractor selection
  const handleSubcontractorChange = (subId) => {
    if (!subId) {
      setAssignedSubId('');
      setAssignedSubName('');
      setAssignedSubPhone('');
      return;
    }
    const sub = subcontractors.find(s => s.subcontractor_id === subId);
    if (sub) {
      setAssignedSubId(sub.subcontractor_id);
      setAssignedSubName(sub.company_name || sub.contact_name || '');
      setAssignedSubPhone(sub.phone || '');
    }
  };
  
  // Auto-set progress to 100 when status is complete
  useEffect(() => {
    if (status === 'complete' && progressPercent < 100) {
      setProgressPercent(100);
    }
  }, [status, progressPercent]);
  
  // Handle save
  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Milestone title is required");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.patch(
        `${API_BASE}/api/milestones/${milestone.milestone_id}`,
        {
          title: title.trim(),
          description: description.trim(),
          start_date: startDate || null,
          target_date: targetDate || null,
          actual_completion_date: status === 'complete' ? (actualCompletionDate || new Date().toISOString().split('T')[0]) : null,
          status,
          progress_percent: progressPercent,
          estimated_cost: parseFloat(estimatedCost) || 0,
          actual_cost: parseFloat(actualCost) || 0,
          assigned_sub_id: assignedSubId || null,
          assigned_sub_name: assignedSubName || null,
          assigned_sub_phone: assignedSubPhone || null,
          notes: notes.trim(),
          order: parseInt(order) || 1
        },
        { withCredentials: true }
      );
      toast.success("Milestone updated successfully!");
      onSaved && onSaved();
    } catch (error) {
      console.error("Failed to update milestone:", error);
      toast.error("Failed to update milestone");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle delete
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API_BASE}/api/milestones/${milestone.milestone_id}`,
        { withCredentials: true }
      );
      toast.success("Milestone deleted");
      onDeleted && onDeleted();
    } catch (error) {
      console.error("Failed to delete milestone:", error);
      toast.error("Failed to delete milestone");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  
  // Calculate days remaining/overdue
  const getDaysInfo = () => {
    if (!targetDate || status === 'complete') return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    const diff = Math.floor((target - today) / (1000 * 60 * 60 * 24));
    
    if (diff < 0) {
      return { text: `${Math.abs(diff)} days overdue`, color: 'text-red-400', bg: 'bg-red-500/20' };
    } else if (diff === 0) {
      return { text: 'Due today', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    } else if (diff <= 7) {
      return { text: `${diff} days remaining`, color: 'text-amber-400', bg: 'bg-amber-500/20' };
    } else {
      return { text: `${diff} days remaining`, color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    }
  };
  
  const daysInfo = getDaysInfo();
  
  const tabs = [
    { id: 'details', label: 'Details', icon: Edit3 },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'financial', label: 'Financial', icon: DollarSign },
    { id: 'assignment', label: 'Crew', icon: Users },
  ];
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="edit-milestone-modal"
    >
      <div className="w-full max-w-2xl rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              statusOptions.find(s => s.value === status)?.color || 'bg-slate-700'
            }`}>
              <Wrench className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Edit Milestone</h3>
              <p className="text-sm text-slate-400">{milestone?.title || 'Milestone'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {daysInfo && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${daysInfo.bg} ${daysInfo.color}`}>
                {daysInfo.text}
              </span>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="px-6 pt-4 border-b border-slate-800 flex gap-1 flex-shrink-0">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-slate-800 text-white border-b-2 border-emerald-500'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        
        {/* Tab Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin', scrollbarColor: '#475569 #1e293b' }}>
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Milestone Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Foundation Complete, Rough Framing"
                  className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                  data-testid="milestone-title-input"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Describe the work involved in this milestone..."
                  className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
                  data-testid="milestone-description-input"
                />
              </div>
              
              {/* Status Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">Status</label>
                <div className="grid grid-cols-5 gap-2">
                  {statusOptions.map(option => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setStatus(option.value)}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          status === option.value
                            ? `${option.color} border-white/30 text-white`
                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                        data-testid={`status-${option.value}`}
                      >
                        <Icon className={`h-5 w-5 mx-auto mb-1 ${status === option.value ? 'text-white' : 'text-slate-500'}`} />
                        <span className="text-xs font-medium block">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Progress Slider */}
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-slate-300">Progress</label>
                  <span className="text-lg font-bold text-emerald-400">{progressPercent}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={progressPercent}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setProgressPercent(val);
                    // Auto-update status based on progress
                    if (val === 100 && status !== 'complete') {
                      setStatus('complete');
                    } else if (val > 0 && val < 100 && status === 'not_started') {
                      setStatus('in_progress');
                    }
                  }}
                  className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  data-testid="progress-slider"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-2">
                  <span>Not Started</span>
                  <span>In Progress</span>
                  <span>Complete</span>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Notes & Updates</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Add notes, updates, or issues..."
                  className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
                  data-testid="milestone-notes-input"
                />
              </div>
              
              {/* Display Order */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Display Order</label>
                <input
                  type="number"
                  min="1"
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  className="w-24 px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                />
                <p className="text-xs text-slate-500 mt-1">Lower numbers appear first in the timeline</p>
              </div>
            </div>
          )}
          
          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    data-testid="start-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Target className="h-4 w-4 inline mr-2" />
                    Target Date
                  </label>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    data-testid="target-date-input"
                  />
                </div>
              </div>
              
              {status === 'complete' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <CheckCircle2 className="h-4 w-4 inline mr-2" />
                    Actual Completion Date
                  </label>
                  <input
                    type="date"
                    value={actualCompletionDate}
                    onChange={(e) => setActualCompletionDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    data-testid="completion-date-input"
                  />
                </div>
              )}
              
              {/* Duration Info */}
              {startDate && targetDate && (
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Schedule Summary</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Duration</p>
                      <p className="text-xl font-bold text-white">
                        {Math.max(0, Math.ceil((new Date(targetDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)))} days
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Start</p>
                      <p className="text-sm font-medium text-slate-300">
                        {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Due</p>
                      <p className="text-sm font-medium text-slate-300">
                        {new Date(targetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Financial Tab */}
          {activeTab === 'financial' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Estimated Cost</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={estimatedCost}
                      onChange={(e) => setEstimatedCost(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                      data-testid="estimated-cost-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Actual Cost</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={actualCost}
                      onChange={(e) => setActualCost(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                      data-testid="actual-cost-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Budget Variance */}
              {(estimatedCost > 0 || actualCost > 0) && (
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Budget Analysis</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Budget</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(estimatedCost)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Actual</p>
                      <p className={`text-xl font-bold ${actualCost > estimatedCost ? 'text-red-400' : 'text-emerald-400'}`}>
                        {formatCurrency(actualCost)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Variance</p>
                      <p className={`text-xl font-bold ${actualCost > estimatedCost ? 'text-red-400' : 'text-emerald-400'}`}>
                        {actualCost > estimatedCost ? '+' : ''}{formatCurrency(actualCost - estimatedCost)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress bar showing budget usage */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Budget Used</span>
                      <span>{estimatedCost > 0 ? Math.round((actualCost / estimatedCost) * 100) : 0}%</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${actualCost > estimatedCost ? 'bg-red-500' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.min((actualCost / (estimatedCost || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Assignment Tab */}
          {activeTab === 'assignment' && (
            <div className="space-y-5">
              {/* Subcontractor Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Assigned Subcontractor</label>
                {subcontractors.length > 0 ? (
                  <select
                    value={assignedSubId}
                    onChange={(e) => handleSubcontractorChange(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    data-testid="subcontractor-select"
                  >
                    <option value="">-- Unassigned --</option>
                    {subcontractors.map(sub => (
                      <option key={sub.subcontractor_id} value={sub.subcontractor_id}>
                        {sub.company_name || sub.contact_name} {sub.trade ? `(${sub.trade})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={assignedSubName}
                      onChange={(e) => setAssignedSubName(e.target.value)}
                      placeholder="Enter subcontractor name"
                      className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                      data-testid="subcontractor-name-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">No subcontractors in system. Enter name manually.</p>
                  </div>
                )}
              </div>
              
              {/* Manual Entry Fields (when using dropdown or manual) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Contact Name</label>
                  <input
                    type="text"
                    value={assignedSubName}
                    onChange={(e) => setAssignedSubName(e.target.value)}
                    placeholder="Subcontractor name"
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={assignedSubPhone}
                    onChange={(e) => setAssignedSubPhone(e.target.value)}
                    placeholder="(555) 555-5555"
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                    data-testid="subcontractor-phone-input"
                  />
                </div>
              </div>
              
              {/* Current Assignment Info */}
              {assignedSubName && (
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                      {assignedSubName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-white">{assignedSubName}</p>
                      {assignedSubPhone && <p className="text-sm text-slate-400">{assignedSubPhone}</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-between items-center flex-shrink-0">
          {/* Delete Button */}
          <div>
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 font-medium transition-all"
                data-testid="delete-milestone-btn"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-red-400">Delete milestone?</span>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-all disabled:opacity-50"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes, Delete'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium transition-all"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          {/* Save Buttons */}
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={submitting || !title.trim()}
              className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
              data-testid="save-milestone-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================= RECORD CLIENT PAYMENT MODAL =================

export const RecordClientPaymentModal = ({ project, paymentSchedule, projectId, onClose, onSaved }) => {
  // Mode: 'invoice' or 'payment'
  const [mode, setMode] = useState('invoice');
  
  // Form fields
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [linkedPayment, setLinkedPayment] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [checkNumber, setCheckNumber] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  
  const [submitting, setSubmitting] = useState(false);
  
  // Suggested amounts from payment schedule
  const pendingPayments = paymentSchedule.filter(p => p.status === 'pending');
  const invoicedPayments = paymentSchedule.filter(p => p.status === 'invoiced');
  
  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setSubmitting(true);
    try {
      if (mode === 'invoice') {
        // If linked to a payment schedule item, use that endpoint
        if (linkedPayment) {
          await axios.post(
            `${API_BASE}/api/payments/${linkedPayment}/generate-invoice`,
            {},
            { withCredentials: true }
          );
          // Then send the invoice
          const invoiceRes = await axios.get(
            `${API_BASE}/api/projects/${projectId}/payment-schedule`,
            { withCredentials: true }
          );
          const payment = invoiceRes.data.find(p => p.payment_id === linkedPayment);
          if (payment?.invoice_id) {
            await axios.post(
              `${API_BASE}/api/invoices/${payment.invoice_id}/send`,
              { recipient: recipientEmail, message: notes },
              { withCredentials: true }
            );
          }
        } else {
          // Create ad-hoc invoice
          await axios.post(
            `${API_BASE}/api/projects/${projectId}/invoices`,
            {
              amount: parseFloat(amount),
              due_date: date,
              notes,
              recipient_email: recipientEmail
            },
            { withCredentials: true }
          );
        }
        toast.success("Invoice sent!");
      } else {
        // Record payment received
        if (linkedPayment) {
          await axios.post(
            `${API_BASE}/api/payments/${linkedPayment}/mark-paid`,
            { 
              paid_date: date, 
              payment_method: paymentMethod, 
              check_number: checkNumber,
              notes 
            },
            { withCredentials: true }
          );
        } else {
          // Record ad-hoc payment
          await axios.post(
            `${API_BASE}/api/projects/${projectId}/payments`,
            {
              amount: parseFloat(amount),
              date,
              payment_method: paymentMethod,
              check_number: checkNumber,
              notes
            },
            { withCredentials: true }
          );
        }
        toast.success("Payment recorded!");
      }
      onSaved();
    } catch (error) {
      toast.error(mode === 'invoice' ? "Failed to send invoice" : "Failed to record payment");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-blue-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Record Client Payment</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-5">
          {/* What happened? Toggle Cards */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">What happened?</label>
            <div className="grid grid-cols-2 gap-3">
              {/* Invoice Sent Card */}
              <button
                type="button"
                onClick={() => setMode('invoice')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  mode === 'invoice'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                  mode === 'invoice' ? 'bg-blue-500/20' : 'bg-slate-700'
                }`}>
                  <FileText className={`h-6 w-6 ${mode === 'invoice' ? 'text-blue-400' : 'text-slate-400'}`} />
                </div>
                <p className={`font-semibold ${mode === 'invoice' ? 'text-white' : 'text-slate-300'}`}>Invoice Sent</p>
                <p className="text-xs text-slate-500 mt-1">Send an invoice to client</p>
              </button>
              
              {/* Payment Received Card */}
              <button
                type="button"
                onClick={() => setMode('payment')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  mode === 'payment'
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                  mode === 'payment' ? 'bg-emerald-500/20' : 'bg-slate-700'
                }`}>
                  <DollarSign className={`h-6 w-6 ${mode === 'payment' ? 'text-emerald-400' : 'text-slate-400'}`} />
                </div>
                <p className={`font-semibold ${mode === 'payment' ? 'text-white' : 'text-slate-300'}`}>Payment Received</p>
                <p className="text-xs text-slate-500 mt-1">Record a client payment</p>
              </button>
            </div>
          </div>
          
          {/* Link to Schedule (if applicable) */}
          {mode === 'invoice' && pendingPayments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Link to scheduled payment (optional)</label>
              <select
                value={linkedPayment}
                onChange={(e) => {
                  setLinkedPayment(e.target.value);
                  const payment = pendingPayments.find(p => p.payment_id === e.target.value);
                  if (payment) setAmount(payment.amount.toString());
                }}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">Custom invoice amount</option>
                {pendingPayments.map(p => (
                  <option key={p.payment_id} value={p.payment_id}>
                    {p.description} - {formatCurrency(p.amount)}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {mode === 'payment' && invoicedPayments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Which invoice was paid?</label>
              <select
                value={linkedPayment}
                onChange={(e) => {
                  setLinkedPayment(e.target.value);
                  const payment = invoicedPayments.find(p => p.payment_id === e.target.value);
                  if (payment) setAmount(payment.amount.toString());
                }}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              >
                <option value="">Other / unlisted payment</option>
                {invoicedPayments.map(p => (
                  <option key={p.payment_id} value={p.payment_id}>
                    {p.invoice_number || p.description} - {formatCurrency(p.amount)}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Amount and Date Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
          
          {/* Invoice-specific fields */}
          {mode === 'invoice' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Recipient Email</label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="client@email.com"
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-blue-500 placeholder-slate-500"
              />
            </div>
          )}
          
          {/* Payment-specific fields */}
          {mode === 'payment' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="check">Check</option>
                  <option value="ach">ACH / Bank Transfer</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {paymentMethod === 'check' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Check Number</label>
                  <input
                    type="text"
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    placeholder="Check #"
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={mode === 'invoice' ? 'Message to include with invoice...' : 'Any notes about this payment...'}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !amount}
            className={`px-6 py-2.5 rounded-lg font-bold transition-all disabled:opacity-50 flex items-center gap-2 ${
              mode === 'invoice'
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? 'Processing...' : mode === 'invoice' ? 'Send Invoice' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= ADD COST MODAL (REDESIGNED) =================

export const AddCostModal = ({ milestones, projectId, onClose, onSaved }) => {
  // Cost type categories
  const costTypes = [
    { id: 'sub_invoice', label: 'Sub Invoice', icon: UserCircle, description: 'Subcontractor invoice' },
    { id: 'material', label: 'Material Bill', icon: Package, description: 'Building materials' },
    { id: 'equipment', label: 'Equipment Rental', icon: Truck, description: 'Tools & equipment' },
    { id: 'permit', label: 'Permit / Fee', icon: FileCheck, description: 'Permits & licenses' },
    { id: 'misc', label: 'Miscellaneous', icon: Receipt, description: 'Other expenses' },
    { id: 'office', label: 'Office', icon: Building2, description: 'Office expenses' },
  ];
  
  const [selectedType, setSelectedType] = useState('sub_invoice');
  const [vendor, setVendor] = useState('');
  const [tradePhase, setTradePhase] = useState('');
  const [customTradePhase, setCustomTradePhase] = useState(''); // For "Other" option
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [linkedMilestone, setLinkedMilestone] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Trade/Phase options based on cost type
  const tradeOptions = {
    sub_invoice: ['Electrical', 'Plumbing', 'HVAC', 'Framing', 'Drywall', 'Painting', 'Roofing', 'Flooring', 'Concrete', 'Other'],
    material: ['Lumber', 'Electrical', 'Plumbing', 'HVAC', 'Drywall', 'Paint', 'Flooring', 'Roofing', 'Hardware', 'Other'],
    equipment: ['Tools', 'Heavy Equipment', 'Scaffolding', 'Vehicles', 'Safety Equipment', 'Other'],
    permit: ['Building Permit', 'Electrical Permit', 'Plumbing Permit', 'Mechanical Permit', 'Inspection Fee', 'Other'],
    misc: ['Cleanup', 'Disposal', 'Delivery', 'Storage', 'Insurance', 'Other'],
    office: ['Software', 'Supplies', 'Phone/Internet', 'Marketing', 'Professional Services', 'Other'],
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReceiptFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setReceiptPreview(ev.target.result);
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };
  
  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReceiptFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setReceiptPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };
  
  const clearReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };
  
  // Get effective trade phase (custom if "Other" selected)
  const getEffectiveTradePhase = () => {
    if (tradePhase === 'Other' && customTradePhase.trim()) {
      return customTradePhase.trim();
    }
    return tradePhase;
  };
  
  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!vendor) {
      toast.error("Please enter vendor/source");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/projects/${projectId}/costs`,
        {
          category: selectedType,
          vendor,
          trade_phase: getEffectiveTradePhase(),
          amount: parseFloat(amount),
          date,
          description,
          invoice_number: invoiceNumber,
          milestone_id: linkedMilestone || null,
        },
        { withCredentials: true }
      );
      toast.success("Cost added!");
      onSaved();
    } catch (error) {
      toast.error("Failed to add cost");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Plus className="h-5 w-5 text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Add Cost</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Cost Type Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">What type of cost?</label>
            <div className="grid grid-cols-3 gap-3">
              {costTypes.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => {
                      setSelectedType(type.id);
                      setTradePhase('');
                    }}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      selectedType === type.id
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
                      selectedType === type.id ? 'bg-emerald-500/20' : 'bg-slate-700'
                    }`}>
                      <Icon className={`h-5 w-5 ${selectedType === type.id ? 'text-emerald-400' : 'text-slate-400'}`} />
                    </div>
                    <p className={`font-medium text-sm ${selectedType === type.id ? 'text-white' : 'text-slate-300'}`}>
                      {type.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Vendor/Source */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {selectedType === 'sub_invoice' ? 'Subcontractor' : 'Vendor / Source'} <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder={selectedType === 'sub_invoice' ? 'Enter subcontractor name' : 'Enter vendor name'}
                className="flex-1 px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
              />
              <button className="px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-emerald-400 hover:bg-slate-700 transition-colors whitespace-nowrap">
                + Add New
              </button>
            </div>
          </div>
          
          {/* Trade/Phase Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {selectedType === 'sub_invoice' ? 'Trade' : 'Category'}
            </label>
            <select
              value={tradePhase}
              onChange={(e) => {
                setTradePhase(e.target.value);
                if (e.target.value !== 'Other') setCustomTradePhase('');
              }}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
            >
              <option value="">Select {selectedType === 'sub_invoice' ? 'trade' : 'category'}...</option>
              {tradeOptions[selectedType]?.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            
            {/* Custom input when "Other" is selected */}
            {tradePhase === 'Other' && (
              <div className="mt-3">
                <input
                  type="text"
                  value={customTradePhase}
                  onChange={(e) => setCustomTradePhase(e.target.value)}
                  placeholder={`Specify ${selectedType === 'sub_invoice' ? 'trade' : 'category'}...`}
                  className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-amber-500/50 text-white focus:outline-none focus:border-amber-500 placeholder-slate-500"
                />
                <p className="text-xs text-amber-400 mt-1">Please specify what type of {selectedType === 'sub_invoice' ? 'trade' : 'category'} this is</p>
              </div>
            )}
          </div>
          
          {/* Amount, Date, Invoice # Row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Amount <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Invoice #</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Optional"
                className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
              />
            </div>
          </div>
          
          {/* Link to Milestone */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Link to Milestone (optional)</label>
            <select
              value={linkedMilestone}
              onChange={(e) => setLinkedMilestone(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
            >
              <option value="">No milestone link</option>
              {milestones.map(m => (
                <option key={m.milestone_id} value={m.milestone_id}>{m.title}</option>
              ))}
            </select>
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What was this cost for?"
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
            />
          </div>
          
          {/* Receipt Upload with Camera */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Receipt / Invoice Photo (optional)</label>
            
            {receiptFile ? (
              <div className="border-2 border-emerald-500/50 rounded-xl p-4 bg-emerald-500/5">
                <div className="flex items-start gap-4">
                  {receiptPreview ? (
                    <img 
                      src={receiptPreview} 
                      alt="Receipt preview" 
                      className="w-24 h-24 object-cover rounded-lg border border-slate-700"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-slate-800 flex items-center justify-center">
                      <FileText className="h-10 w-10 text-emerald-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white truncate">{receiptFile.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{(receiptFile.size / 1024).toFixed(1)} KB</p>
                    <button 
                      onClick={clearReceipt}
                      className="mt-2 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-medium transition-colors flex items-center gap-1"
                    >
                      <X className="h-3 w-3" />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {/* Camera Capture Button - Primary for mobile */}
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="border-2 border-dashed border-emerald-500/50 rounded-xl p-6 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-500/5 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3 group-hover:bg-emerald-500/30 transition-colors">
                    <Camera className="h-6 w-6 text-emerald-400" />
                  </div>
                  <p className="text-sm font-medium text-emerald-400">Take Photo</p>
                  <p className="text-xs text-slate-500 mt-1">Use camera</p>
                </button>
                
                {/* File Upload Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-700 rounded-xl p-6 text-center cursor-pointer hover:border-slate-600 hover:bg-slate-800/50 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center mx-auto mb-3 group-hover:bg-slate-600 transition-colors">
                    <Upload className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-300">Upload File</p>
                  <p className="text-xs text-slate-500 mt-1">PNG, JPG, PDF</p>
                </button>
              </div>
            )}
            
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCameraCapture}
              className="hidden"
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3 flex-shrink-0">
          <button 
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !amount || !vendor}
            className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Submitting...' : 'Submit Cost'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= ASSIGN CREW MODAL =================

export const AssignCrewModal = ({ milestone, projectId, onClose, onSaved }) => {
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSub, setSelectedSub] = useState(milestone?.assigned_sub_id || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTrade, setFilterTrade] = useState('');
  
  // Fetch subcontractors
  useEffect(() => {
    const fetchSubs = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/subcontractors`, { withCredentials: true });
        setSubcontractors(response.data || []);
      } catch (error) {
        console.error('Failed to load subcontractors:', error);
        toast.error('Failed to load subcontractors');
      } finally {
        setLoading(false);
      }
    };
    fetchSubs();
  }, []);
  
  // Get unique trades for filter
  const trades = [...new Set(subcontractors.map(s => s.trade).filter(Boolean))].sort();
  
  // Filter subcontractors
  const filteredSubs = subcontractors.filter(sub => {
    const matchesSearch = !searchTerm || 
      sub.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.contact_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTrade = !filterTrade || sub.trade === filterTrade;
    return matchesSearch && matchesTrade && sub.status !== 'inactive';
  });
  
  const handleAssign = async () => {
    if (!selectedSub) {
      toast.error('Please select a subcontractor');
      return;
    }
    
    const sub = subcontractors.find(s => s.subcontractor_id === selectedSub);
    if (!sub) return;
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/milestones/${milestone.milestone_id}/assign-crew`,
        {
          subcontractor_id: sub.subcontractor_id,
          subcontractor_name: sub.company_name || sub.contact_name,
          subcontractor_phone: sub.phone,
          subcontractor_email: sub.email,
        },
        { withCredentials: true }
      );
      toast.success(`${sub.company_name || sub.contact_name} assigned to ${milestone.title}`);
      onSaved();
    } catch (error) {
      toast.error('Failed to assign subcontractor');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleRemove = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/milestones/${milestone.milestone_id}/assign-crew`,
        {
          subcontractor_id: null,
          subcontractor_name: null,
          subcontractor_phone: null,
          subcontractor_email: null,
        },
        { withCredentials: true }
      );
      toast.success('Subcontractor removed from milestone');
      onSaved();
    } catch (error) {
      toast.error('Failed to remove subcontractor');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <UserCircle className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Assign Crew</h3>
              <p className="text-sm text-slate-400">{milestone?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Current Assignment */}
        {milestone?.assigned_sub_name && (
          <div className="px-6 py-4 bg-orange-500/10 border-b border-orange-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                  {milestone.assigned_sub_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-white">{milestone.assigned_sub_name}</p>
                  <p className="text-sm text-slate-400">Currently assigned</p>
                </div>
              </div>
              <button
                onClick={handleRemove}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-medium transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        )}
        
        {/* Search & Filter */}
        <div className="px-6 py-4 border-b border-slate-800 space-y-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search subcontractors..."
            className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
          />
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilterTrade('')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                !filterTrade ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              All Trades
            </button>
            {trades.slice(0, 6).map(trade => (
              <button
                key={trade}
                onClick={() => setFilterTrade(trade)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  filterTrade === trade ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {trade}
              </button>
            ))}
          </div>
        </div>
        
        {/* Subcontractor List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : filteredSubs.length === 0 ? (
            <div className="text-center py-12">
              <UserCircle className="h-12 w-12 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-500">No subcontractors found</p>
              <p className="text-sm text-slate-600 mt-1">Add subcontractors in the Subcontractors page</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSubs.map(sub => (
                <button
                  key={sub.subcontractor_id}
                  onClick={() => setSelectedSub(sub.subcontractor_id)}
                  className={`w-full p-4 rounded-lg border transition-all text-left ${
                    selectedSub === sub.subcontractor_id
                      ? 'bg-emerald-500/10 border-emerald-500'
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                      selectedSub === sub.subcontractor_id ? 'bg-emerald-500' : 'bg-slate-700'
                    }`}>
                      {(sub.company_name || sub.contact_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{sub.company_name || sub.contact_name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {sub.trade && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
                            {sub.trade}
                          </span>
                        )}
                        {sub.contact_name && sub.company_name && (
                          <span className="text-xs text-slate-500">{sub.contact_name}</span>
                        )}
                      </div>
                      {sub.phone && (
                        <p className="text-xs text-slate-500 mt-1">{sub.phone}</p>
                      )}
                    </div>
                    {selectedSub === sub.subcontractor_id && (
                      <Check className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-between items-center flex-shrink-0">
          <p className="text-sm text-slate-500">
            {filteredSubs.length} subcontractor{filteredSubs.length !== 1 ? 's' : ''} available
          </p>
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={submitting || !selectedSub}
              className="px-6 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {submitting ? 'Assigning...' : 'Assign Crew'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================= BULK EDIT MILESTONE MODAL =================

export const BulkEditMilestoneModal = ({ milestones, projectId, subcontractors = [], onClose, onSaved }) => {
  // What to update
  const [updateFields, setUpdateFields] = useState({
    status: false,
    progress: false,
    shiftDates: false,
    crew: false,
  });
  
  // Update values
  const [status, setStatus] = useState('in_progress');
  const [progressPercent, setProgressPercent] = useState(50);
  const [shiftDays, setShiftDays] = useState(0);
  const [shiftDirection, setShiftDirection] = useState('forward'); // 'forward' or 'backward'
  const [assignedSubId, setAssignedSubId] = useState('');
  const [assignedSubName, setAssignedSubName] = useState('');
  
  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0); // For progress tracking
  
  const statusOptions = [
    { value: 'not_started', label: 'Not Started', color: 'bg-slate-600', icon: Clock },
    { value: 'in_progress', label: 'In Progress', color: 'bg-blue-600', icon: Target },
    { value: 'complete', label: 'Complete', color: 'bg-emerald-600', icon: CheckCircle2 },
    { value: 'on_hold', label: 'On Hold', color: 'bg-amber-600', icon: Pause },
    { value: 'delayed', label: 'Delayed', color: 'bg-red-600', icon: AlertCircle },
  ];
  
  // Handle subcontractor selection
  const handleSubcontractorChange = (subId) => {
    if (!subId) {
      setAssignedSubId('');
      setAssignedSubName('');
      return;
    }
    const sub = subcontractors.find(s => s.subcontractor_id === subId);
    if (sub) {
      setAssignedSubId(sub.subcontractor_id);
      setAssignedSubName(sub.company_name || sub.contact_name || '');
    }
  };
  
  // Calculate shifted date
  const shiftDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const days = shiftDirection === 'forward' ? shiftDays : -shiftDays;
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };
  
  // Handle bulk update
  const handleBulkUpdate = async () => {
    const updates = {};
    
    if (updateFields.status) {
      updates.status = status;
      if (status === 'complete') {
        updates.progress_percent = 100;
      }
    }
    
    if (updateFields.progress) {
      updates.progress_percent = progressPercent;
      if (progressPercent === 100) {
        updates.status = 'complete';
      } else if (progressPercent > 0 && updates.status !== 'complete') {
        updates.status = updates.status || 'in_progress';
      }
    }
    
    if (updateFields.crew && assignedSubId) {
      updates.assigned_sub_id = assignedSubId;
      updates.assigned_sub_name = assignedSubName;
    }
    
    // Count what we're updating
    const updateCount = Object.values(updateFields).filter(Boolean).length;
    if (updateCount === 0) {
      toast.error("Please select at least one field to update");
      return;
    }
    
    setSubmitting(true);
    setCurrentStep(0);
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (let i = 0; i < milestones.length; i++) {
        const milestone = milestones[i];
        setCurrentStep(i + 1);
        
        const milestoneUpdates = { ...updates };
        
        // Handle date shifting per milestone
        if (updateFields.shiftDates && shiftDays > 0) {
          if (milestone.start_date) {
            milestoneUpdates.start_date = shiftDate(milestone.start_date);
          }
          if (milestone.target_date) {
            milestoneUpdates.target_date = shiftDate(milestone.target_date);
          }
        }
        
        try {
          await axios.patch(
            `${API_BASE}/api/milestones/${milestone.milestone_id}`,
            milestoneUpdates,
            { withCredentials: true }
          );
          successCount++;
        } catch (err) {
          console.error(`Failed to update milestone ${milestone.title}:`, err);
          failCount++;
        }
      }
      
      if (failCount === 0) {
        toast.success(`Successfully updated ${successCount} milestone${successCount > 1 ? 's' : ''}`);
      } else {
        toast.warning(`Updated ${successCount} milestones, ${failCount} failed`);
      }
      
      onSaved && onSaved();
    } catch (error) {
      console.error("Bulk update failed:", error);
      toast.error("Bulk update failed");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Get summary of what will be updated
  const getUpdateSummary = () => {
    const items = [];
    if (updateFields.status) items.push(`Status → ${statusOptions.find(s => s.value === status)?.label}`);
    if (updateFields.progress) items.push(`Progress → ${progressPercent}%`);
    if (updateFields.shiftDates && shiftDays > 0) items.push(`Shift dates ${shiftDirection} by ${shiftDays} days`);
    if (updateFields.crew && assignedSubName) items.push(`Assign to ${assignedSubName}`);
    return items;
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="bulk-edit-milestone-modal"
    >
      <div className="w-full max-w-2xl rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
              <CheckSquare className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Bulk Edit Milestones</h3>
              <p className="text-sm text-slate-400">{milestones.length} milestone{milestones.length > 1 ? 's' : ''} selected</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" style={{ scrollbarWidth: 'thin', scrollbarColor: '#475569 #1e293b' }}>
          {/* Selected Milestones Preview */}
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Selected Milestones</h4>
            <div className="flex flex-wrap gap-2">
              {milestones.map(m => (
                <span key={m.milestone_id} className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium">
                  {m.title}
                </span>
              ))}
            </div>
          </div>
          
          {/* Update Options */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">What would you like to update?</h4>
            
            {/* Status Update */}
            <div className={`p-4 rounded-lg border transition-all ${
              updateFields.status ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
            }`}>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={updateFields.status}
                  onChange={(e) => setUpdateFields({ ...updateFields, status: e.target.checked })}
                  className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="font-medium text-white">Update Status</span>
              </label>
              
              {updateFields.status && (
                <div className="grid grid-cols-5 gap-2 ml-8">
                  {statusOptions.map(option => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setStatus(option.value)}
                        className={`p-2 rounded-lg border-2 transition-all text-center ${
                          status === option.value
                            ? `${option.color} border-white/30 text-white`
                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        <Icon className={`h-4 w-4 mx-auto mb-1 ${status === option.value ? 'text-white' : 'text-slate-500'}`} />
                        <span className="text-xs font-medium block">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Progress Update */}
            <div className={`p-4 rounded-lg border transition-all ${
              updateFields.progress ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
            }`}>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={updateFields.progress}
                  onChange={(e) => setUpdateFields({ ...updateFields, progress: e.target.checked })}
                  className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="font-medium text-white">Update Progress</span>
              </label>
              
              {updateFields.progress && (
                <div className="ml-8">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-slate-400">Set progress to:</span>
                    <span className="text-lg font-bold text-emerald-400">{progressPercent}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={progressPercent}
                    onChange={(e) => setProgressPercent(parseInt(e.target.value))}
                    className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Shift Dates */}
            <div className={`p-4 rounded-lg border transition-all ${
              updateFields.shiftDates ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
            }`}>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={updateFields.shiftDates}
                  onChange={(e) => setUpdateFields({ ...updateFields, shiftDates: e.target.checked })}
                  className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="font-medium text-white">Shift Dates</span>
              </label>
              
              {updateFields.shiftDates && (
                <div className="ml-8 flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShiftDirection('backward')}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        shiftDirection === 'backward' 
                          ? 'bg-amber-600 border-amber-500 text-white' 
                          : 'bg-slate-800 border-slate-700 text-slate-400'
                      }`}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShiftDirection('forward')}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        shiftDirection === 'forward' 
                          ? 'bg-emerald-600 border-emerald-500 text-white' 
                          : 'bg-slate-800 border-slate-700 text-slate-400'
                      }`}
                    >
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={shiftDays}
                      onChange={(e) => setShiftDays(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-20 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-center focus:outline-none focus:border-emerald-500"
                    />
                    <span className="text-slate-400">days {shiftDirection}</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Assign Crew */}
            <div className={`p-4 rounded-lg border transition-all ${
              updateFields.crew ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
            }`}>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={updateFields.crew}
                  onChange={(e) => setUpdateFields({ ...updateFields, crew: e.target.checked })}
                  className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="font-medium text-white">Assign Crew</span>
              </label>
              
              {updateFields.crew && (
                <div className="ml-8">
                  {subcontractors.length > 0 ? (
                    <select
                      value={assignedSubId}
                      onChange={(e) => handleSubcontractorChange(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    >
                      <option value="">-- Select Subcontractor --</option>
                      {subcontractors.map(sub => (
                        <option key={sub.subcontractor_id} value={sub.subcontractor_id}>
                          {sub.company_name || sub.contact_name} {sub.trade ? `(${sub.trade})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-slate-500">No subcontractors available. Add subcontractors first.</p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Update Summary */}
          {getUpdateSummary().length > 0 && (
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <h4 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-2">Update Summary</h4>
              <ul className="space-y-1">
                {getUpdateSummary().map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="h-4 w-4 text-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-sm text-slate-400">
                This will update <strong className="text-white">{milestones.length}</strong> milestone{milestones.length > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-between items-center flex-shrink-0">
          {submitting && (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
              <span className="text-sm text-slate-400">
                Updating {currentStep}/{milestones.length}...
              </span>
            </div>
          )}
          {!submitting && <div></div>}
          
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              disabled={submitting}
              className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkUpdate}
              disabled={submitting || getUpdateSummary().length === 0}
              className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
              data-testid="bulk-update-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Update {milestones.length} Milestone{milestones.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
